import React, { FC, useEffect } from "react";
import { debounce } from "lodash";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import {
  ActiveInactiveButton,
  CustomButton,
  CustomLabel,
  FlexContainer,
  ITableHeaders,
  Loader,
  PlaceholderWrapper,
  SapphireBlueButton,
  TableHeader,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { AuthService } from "../../../shared/services";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  NavPills,
  TableGrid,
} from "../../../shared/components";
import { FundingActivityStatus, GridNames } from "../../../shared/enums";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import FundingStore from "../../../shared/store/FundingStore";
import { INavItem } from "../../../shared/interfaces";
import { FundingPayoutsGridModel } from "../../../shared/models/FundingPayoutsModel";
import FundingService from "../../../shared/services/FundingService";
import { ActivityWrapper } from "../../dashboard";
import { CustomLine, MainOuterCard } from "../../../shared/styles";
import AccountsIcon from "../../../../../../images/funding/accounts-funding.svg";
import PayoutIcon from "../../../../../../images/common/funding-blue.svg";
import UpcomingIcon from "../../../../../../images/common/clock-slate-blue.svg";
import TransitIcon from "../../../../../../images/common/share-slate-blue.svg";
import RecentIcon from "../../../../../../images/common/download-slate-blue.svg";
import MindMeLogo from "../../../../../../images/common/mindMe-solo-logo.svg";
import StripeLogo from "../../../../../../images/common/stripe-logo.svg";
import FundingPlaceholderIcon from "../../../../../../images/placeholders/funding-light-grey.svg";

type IProps = {
  fundingStore: FundingStore;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const FundingSummary: FC<IProps> = ({
  fundingStore,
  navigate,
  itemStore,
  merchantAccountStore,
  rolesAndPermissionStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [showGridLoader, setGridShowLoader] = React.useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(false);
  const [activeKey, setActiveKey] = React.useState("summary");

  useEffect(() => {
    loadData();
    loadGrid(
      fundingStore.pagination.pageNumber,
      fundingStore.pagination.pageSize,
      fundingStore.searchValue
    );
    fundingStore.searchSubject.subscribe(() => {
      debounceSearch();
    });
  }, []);

  const showAnyLoader = showLoader || showGridLoader;
  function loadData() {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    FundingService.getFundings(AuthService.authData.accountId).subscribe({
      next: (response) => {
        setShowLoader(false);
        fundingStore.deserializeSummaryPageAPIResponse(response.result);
      },
      error: (error) => {
        setShowLoader(false);
        console.log(error);
      },
    });
  }

  function loadGrid(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setGridShowLoader(true);
    FundingService.getPayouts(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        setGridShowLoader(false);
        fundingStore.deserializePayoutAPIResponse(
          response.result || [],
          response.meta
        );
      },
      error: (error) => {
        setGridShowLoader(false);
        console.log(error);
      },
    });
  }

  const debounceSearch = debounce(
    () =>
      loadGrid(
        fundingStore.pagination.pageNumber,
        fundingStore.pagination.pageSize,
        fundingStore.searchValue
      ),
    500
  );

  const navPills = (): INavItem[] => {
    return [
      {
        key: "summary",
        title: "SUMMARY",
      },
      {
        key: "payouts",
        title: "PAYOUTS",
      },
    ];
  };

  const updateActiveKey = (key: string) => {
    setActiveKey(key);
  };

  const renderNavPills = () => {
    return (
      <>
        <FlexContainer
          alignItems="center"
          padding="0px 0px 40px"
          justifyContent="space-between"
          className="position-relative"
        >
          <CustomLabel
            color="#2C3E50"
            fontSize="30px"
            fontWeight="600"
            lineHeight="40px"
          >
            Funding
          </CustomLabel>
          <NavPills
            defaultActiveKey="summary"
            pills={navPills()}
            activeKey={"summary"}
            onSelect={(key) => updateActiveKey(key)}
            className="navpills-centered"
          />
        </FlexContainer>
      </>
    );
  };

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadGrid(pageNumber, pageSize, fundingStore.searchValue);
  }

  function columns() {
    const columns: ITableHeaders<FundingPayoutsGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: FundingPayoutsGridModel) => {
          const colors = data.getColorsByStatus();
          return (
            <CustomButton
              minWidth={"90px"}
              padding="6px 10px"
              borderRadius="4px !important"
              color={colors.color}
              bgColor={colors.bgColor}
              className="status-btn"
            >
              {data.status}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "payoutNo",
        title: "Payout No.",
      },
      {
        dataKey: "createdDate",
        title: "Date",
        onCellRender: (data: FundingPayoutsGridModel) => {
          return UtilityMethods.getDayCommaMonthDateCommaYear(
            data.createdDate,
            false
          );
        },
      },
      {
        dataKey: "totalPayout",
        title: "Total Payout",
        onCellRender: (data: FundingPayoutsGridModel) => {
          return AccountSettingsStore.formatAmountText(data.totalPayout, true);
        },
      },
      {
        dataKey: "merchantAccount",
        title: "Merchant Account",
        onCellRender: (data: FundingPayoutsGridModel) => {
          return data.merchantAccount?.name;
        },
      },
    ];
    return columns;
  }

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide itemStore={itemStore} />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{null}</>}
          merchantAccountStore={merchantAccountStore}
          onlyShowNotificationBox={isNoData}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showAnyLoader}
          className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function goToDetailPage(merchantAccountId: string, id: string) {
    if (!merchantAccountId || !id) return;
    navigate(`/app/funding/detail/${merchantAccountId}/${id}`);
  }

  const isNoData =
    firstTimeLoad &&
    fundingStore.searchValue === "" &&
    !showAnyLoader &&
    fundingStore.pagination.totalItemCount === 0;

  const NoGridData = fundingStore.gridData.length === 0;

  const renderSummaryContent = () => {
    const renderPayoutContent = (
      heading: string,
      records: FundingPayoutsGridModel[],
      count: number = 0,
      icon: string = UpcomingIcon
    ) => {
      const data = count > 0 ? records.slice(0, count) : records;
      return (
        <FlexContainer fxLayout="column" className="payout-category">
          <div>
            <FlexContainer
              alignItems="center"
              spacing={15}
              padding="0px 0px 10px"
            >
              <img src={icon} className="payout-sub-header-icon" />
              <div className="activity-card-sub-header">{heading}</div>
            </FlexContainer>
          </div>
          <div className="payouts-list">
            {data.map((payout) => (
              <FlexContainer
                key={payout.id}
                fxLayout="row"
                spacing={30}
                className="payout-item"
                onClick={() =>
                  goToDetailPage(
                    payout.merchantAccount?.merchantAccountId,
                    payout.id
                  )
                }
              >
                <div className="payout-status">
                  <CustomButton
                    className="payout-status-btn"
                    bgColor={payout.getColorsByStatus().bgColor}
                    color={payout.getColorsByStatus().color}
                  >
                    {payout.status}
                  </CustomButton>
                </div>
                <div className="payout-details">
                  <CustomLabel
                    color={payout.totalPayout < 0 ? "#FF0000" : "#000"}
                  >
                    {AccountSettingsStore.formatAmountText(
                      payout.totalPayout,
                      true
                    )}
                  </CustomLabel>
                </div>
                <div className="payout-date">
                  <CustomLabel>
                    {UtilityMethods.getDayCommaMonthDateCommaYear(
                      payout.createdDate,
                      false
                    )}
                  </CustomLabel>
                </div>
                <div className="payout-merchant-account">
                  <CustomLabel>
                    {payout.merchantAccount.name}, MindMe
                  </CustomLabel>
                  {/* this is static data */}
                </div>
              </FlexContainer>
            ))}
          </div>
        </FlexContainer>
      );
    };
    const upcomingPayouts = fundingStore.filterPayouts([
      FundingActivityStatus.Scheduled,
    ]);
    const inTransitPayouts = fundingStore.filterPayouts([
      FundingActivityStatus.InTansit,
    ]);
    const recentPayouts = fundingStore.filterPayouts(
      [FundingActivityStatus.Scheduled, FundingActivityStatus.InTansit],
      false
    );
    return (
      <FlexContainer
        fxLayout="column"
        className={`funding-summary-block`}
        spacing={60}
      >
        {/* Accounts Section */}
        <FlexContainer
          fxLayout="column"
          className="accounts-section"
          spacing={30}
        >
          <div>
            <FlexContainer
              alignItems="center"
              spacing={10}
              padding="0px 0px 5px"
            >
              <img src={AccountsIcon} />
              <div className="activity-card-header">Accounts</div>
            </FlexContainer>
          </div>
          <div className="full-width">
            <FlexContainer className="accounts-container" fxWrap="wrap">
              {fundingStore.merchantAccounts.map((account) => (
                <>
                  <FlexContainer
                    fxLayout="row"
                    spacing={30}
                    className="account-details funding-half-section "
                  >
                    <CustomLine
                      height="130px"
                      width="1px"
                      bgColor="#ddd"
                      className="funding-custom-line"
                      margin="0px 50px 0px 0px"
                    />
                    <FlexContainer fxLayout="column" spacing={25}>
                      <CustomLabel fontSize={"16px"}>
                        Merchant Account
                      </CustomLabel>
                      <img src={MindMeLogo} className="funding-accounts-logo" />
                      <FlexContainer
                        fxLayout="column"
                        spacing={2}
                        className="funding-address"
                      >
                        <CustomLabel>{account.name}</CustomLabel>
                        {account.bankName && (
                          <CustomLabel>{account.bankName}</CustomLabel>
                        )}
                        {account.accountEndingNumber && (
                          <CustomLabel>
                            {`Account ending ${account.accountEndingNumber}`}
                          </CustomLabel>
                        )}
                      </FlexContainer>
                    </FlexContainer>
                    <FlexContainer fxLayout="column" spacing={25}>
                      <CustomLabel fontSize={"16px"}>Balance (USD)</CustomLabel>
                      <CustomLabel
                        fontSize={"30px"}
                        lineHeight="36px"
                        fontWeight={"600"}
                        color="#2C3E50"
                      >
                        $
                        {AccountSettingsStore.formatAmount(
                          account.balance,
                          true
                        )}
                      </CustomLabel>
                    </FlexContainer>
                  </FlexContainer>
                </>
              ))}
            </FlexContainer>
          </div>
        </FlexContainer>
        {/* Payouts Section */}
        {fundingStore.payouts.length > 0 && (
          <FlexContainer
            fxLayout="column"
            className="payouts-section"
            spacing={30}
          >
            <div>
              <FlexContainer
                alignItems="center"
                spacing={10}
                padding="0px 0px 5px"
              >
                <img src={PayoutIcon} />
                <div className="activity-card-header">Payouts</div>
              </FlexContainer>
            </div>
            <div className="funding-smaller-grids">
              <FlexContainer
                alignItems="center"
                spacing={15}
                padding="0px 0px 20px"
              >
                {/* Upcoming */}
                {upcomingPayouts.length > 0 &&
                  renderPayoutContent(
                    "Upcoming",
                    upcomingPayouts,
                    0,
                    UpcomingIcon
                  )}
                {/* In Transit */}
                {inTransitPayouts.length > 0 &&
                  renderPayoutContent(
                    "In Transit",
                    inTransitPayouts,
                    0,
                    TransitIcon
                  )}
                {/* Recent */}
                {recentPayouts.length > 0 &&
                  renderPayoutContent("Recent", recentPayouts, 7, RecentIcon)}
              </FlexContainer>
            </div>
          </FlexContainer>
        )}
      </FlexContainer>
    );
  };

  const fundingStoreHeight =
    fundingStore.merchantAccounts.length <= 0
      ? "funding-full-height"
      : "funding-inherit-height";
  return renderDetails(
    <ActivityWrapper className="funding-summary-wrapper">
      <div className={fundingStoreHeight}>
        <FlexContainer className={`db-min-width ${fundingStoreHeight}`}>
          <MainOuterCard width="50%" className="main-card-min-width">
            <FlexContainer fxLayout="column" className={fundingStoreHeight}>
              {fundingStore.merchantAccounts.length > 0 ? (
                <>
                  {renderNavPills()}
                  {showAnyLoader ? (
                    new Loader(true).loaderComponent
                  ) : activeKey === "summary" ? (
                    renderSummaryContent()
                  ) : (
                    <div>
                      <FlexContainer
                        fxLayout="column"
                        className={`funding-summary-block funding-payout-block ${
                          NoGridData ? "funding-summary-plh" : ""
                        }`}
                        spacing={10}
                      >
                        {!NoGridData && (
                          <div>
                            <FlexContainer
                              fxLayout="column"
                              className="accounts-section"
                              spacing={30}
                            >
                              <div>
                                <FlexContainer
                                  alignItems="center"
                                  spacing={10}
                                  padding="0px 0px 5px"
                                >
                                  <img src={PayoutIcon} />
                                  <div className="activity-card-header">
                                    Payouts{" "}
                                    <span
                                      style={{
                                        fontWeight: "600",
                                        color: "#ff5900",
                                      }}
                                    >
                                      {fundingStore.pagination.totalItemCount}
                                    </span>
                                  </div>
                                </FlexContainer>
                              </div>
                            </FlexContainer>
                          </div>
                        )}
                        <TableGrid
                          onRowClick={(data: FundingPayoutsGridModel) =>
                            goToDetailPage(
                              data.merchantAccount?.merchantAccountId,
                              data.id
                            )
                          }
                          cellSpacing={20}
                          onPaginationChange={(
                            pageNumber: number,
                            pageSize: number
                          ) => onPaginationChange(pageNumber, pageSize)}
                          pagination={fundingStore.pagination}
                          data={fundingStore.gridData}
                          headers={columns()}
                          name={GridNames.PayOuts}
                          height={
                            fundingStore.showFilter ? "calc(100vh - 499px)" : ""
                          }
                          hideTable={NoGridData}
                          wrapperClassName={`${NoGridData ? "funding-payout-plh" : ""} funding-payouts-grid`}
                        />
                      </FlexContainer>
                    </div>
                  )}
                </>
              ) : (
                <PlaceholderWrapper className="funding-plh-wrapper">
                  <FlexContainer className="plh-main-section">
                    <img
                      src={FundingPlaceholderIcon}
                      alt="icon"
                      className={`plh-main-icon `}
                    />

                    <CustomLabel className="plh-label">
                      No Funding Available
                    </CustomLabel>

                    <CustomLabel className="plh-description">
                      Funding entries are automatically created when financial
                      sources or <br /> destinations are defined. Once
                      configured, related transactions will appear here.
                    </CustomLabel>

                    {/* <SapphireBlueButton primary className="plh-btn">
                      New
                    </SapphireBlueButton> */}
                  </FlexContainer>
                </PlaceholderWrapper>
              )}
            </FlexContainer>
          </MainOuterCard>
        </FlexContainer>
      </div>
    </ActivityWrapper>
  );
};

export default withRouter(observer(FundingSummary));
